import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import authgear from "../authgear";
import Header from "../components/Header";
import PixelIt from "../lib/pixelit";
import { getMountainDate, formatWorkoutText } from "../utils/formatWorkout";

const base_url = process.env.NODE_ENV === "production" ? "https://hooks-28xd.onrender.com" : "http://localhost:4000";

const getVideoDimensions = (file) => new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
        resolve({ width: video.videoWidth, height: video.videoHeight });
        URL.revokeObjectURL(video.src);
    };
    video.onerror = reject;
    video.src = URL.createObjectURL(file);
});

const wrapText = (ctx, text, maxWidth) => {
    const lines = [];
    text.split("\n").forEach(paragraph => {
        const words = paragraph.split(" ");
        let line = "";
        words.forEach(word => {
            const test = line ? `${line} ${word}` : word;
            if (ctx.measureText(test).width > maxWidth && line) {
                lines.push(line);
                line = word;
            } else {
                line = test;
            }
        });
        lines.push(line);
    });
    return lines;
};

const buildOverlayCanvas = (text, width, height) => {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");

    const fontSize = Math.max(18, Math.round(width * 0.035));
    ctx.font = `bold ${fontSize}px Verdana, sans-serif`;
    const padding = fontSize;
    const lines = wrapText(ctx, text, width - padding * 2).slice(0, 8);
    const lineHeight = fontSize * 1.3;
    const boxHeight = lines.length * lineHeight + padding * 2;

    ctx.fillStyle = "rgba(0,0,0,0.55)";
    ctx.fillRect(0, height - boxHeight, width, boxHeight);

    ctx.fillStyle = "#ffffff";
    ctx.font = `bold ${fontSize}px Verdana, sans-serif`;
    ctx.textBaseline = "top";
    lines.forEach((line, i) => {
        ctx.fillText(line, padding, height - boxHeight + padding + i * lineHeight);
    });

    return canvas;
};

const canvasToBlob = (canvas) => new Promise(resolve => canvas.toBlob(resolve, "image/png"));

const TikTokPost = () => {
    const navigate = useNavigate();
    const canvasRef = useRef(null);
    const imgRef = useRef(null);

    const [date, setDate] = useState(getMountainDate());
    const [workout, setWorkout] = useState(null);
    const [text, setText] = useState("");
    const [connected, setConnected] = useState(null);

    const [mediaFile, setMediaFile] = useState(null);
    const [mediaType, setMediaType] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [pixelPreviewUrl, setPixelPreviewUrl] = useState(null);

    const [musicAssets, setMusicAssets] = useState([]);
    const [musicAssetId, setMusicAssetId] = useState("");
    const [newTrackName, setNewTrackName] = useState("");
    const [newTrackUrl, setNewTrackUrl] = useState("");

    const [composedUrl, setComposedUrl] = useState(null);
    const [status, setStatus] = useState("");
    const [loading, setLoading] = useState({ workout: false, compose: false, post: false, track: false });

    const authHeaders = useCallback(async () => {
        await authgear.refreshAccessTokenIfNeeded();
        return { Authorization: `Bearer ${authgear.accessToken}` };
    }, []);

    const loadWorkout = useCallback(async (d) => {
        setLoading(l => ({ ...l, workout: true }));
        try {
            const { data } = await axios.get(`${base_url}/api/workouts/daily/${d}`);
            if (data.success && data.workout) {
                setWorkout(data.workout);
                setText(formatWorkoutText(data.workout, d));
            } else {
                setWorkout(null);
                setText("");
            }
        } catch (e) {
            setWorkout(null);
        } finally {
            setLoading(l => ({ ...l, workout: false }));
        }
    }, []);

    const loadStatus = useCallback(async () => {
        try {
            const headers = await authHeaders();
            const { data } = await axios.get(`${base_url}/tiktok/status`, { headers });
            setConnected(data.connected);
        } catch (e) {
            setConnected(false);
        }
    }, [authHeaders]);

    const loadMusic = useCallback(async () => {
        try {
            const headers = await authHeaders();
            const { data } = await axios.get(`${base_url}/assets`, { headers, params: { type: "audio" } });
            setMusicAssets(data);
        } catch (e) {
            setMusicAssets([]);
        }
    }, [authHeaders]);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (authgear.sessionState !== "AUTHENTICATED") {
            navigate("/login");
            return;
        }
        loadWorkout(date);
        loadStatus();
        loadMusic();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    const handleDateChange = (e) => {
        const d = e.target.value;
        setDate(d);
        loadWorkout(d);
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setComposedUrl(null);
        setStatus("");
        setMediaFile(file);
        setPixelPreviewUrl(null);

        if (file.type.startsWith("image/")) {
            setMediaType("image");
            setPreviewUrl(URL.createObjectURL(file));
        } else if (file.type.startsWith("video/")) {
            setMediaType("video");
            setPreviewUrl(URL.createObjectURL(file));
        } else {
            setStatus("Please select an image or video file.");
            setMediaFile(null);
        }
    };

    useEffect(() => {
        if (mediaType !== "image" || !previewUrl || !imgRef.current || !canvasRef.current) return;
        const img = imgRef.current;
        img.onload = () => {
            const px = new PixelIt({ to: canvasRef.current, from: img, scale: 8 });
            px.pixelate();
            setPixelPreviewUrl(canvasRef.current.toDataURL("image/png"));
        };
        img.src = previewUrl;
    }, [mediaType, previewUrl]);

    const handleAddTrack = async (e) => {
        e.preventDefault();
        if (!newTrackName.trim() || !newTrackUrl.trim()) return;
        setLoading(l => ({ ...l, track: true }));
        try {
            const headers = await authHeaders();
            await axios.post(`${base_url}/assets`, {
                name: newTrackName.trim(), type: "audio", storageType: "url", url: newTrackUrl.trim()
            }, { headers });
            setNewTrackName("");
            setNewTrackUrl("");
            await loadMusic();
        } catch (e) {
            setStatus(`Failed to add track: ${e.response?.data?.message || e.message}`);
        } finally {
            setLoading(l => ({ ...l, track: false }));
        }
    };

    const handleCompose = async () => {
        if (!mediaFile || !mediaType) return;
        setLoading(l => ({ ...l, compose: true }));
        setStatus("");
        setComposedUrl(null);
        try {
            let dims;
            let mediaBlob = mediaFile;
            if (mediaType === "image") {
                if (!canvasRef.current) throw new Error("Pixel preview not ready yet");
                dims = { width: canvasRef.current.width, height: canvasRef.current.height };
                mediaBlob = await canvasToBlob(canvasRef.current);
            } else {
                dims = await getVideoDimensions(mediaFile);
            }

            const overlayCanvas = buildOverlayCanvas(text, dims.width, dims.height);
            const overlayBlob = await canvasToBlob(overlayCanvas);

            const form = new FormData();
            form.append("media", mediaBlob, mediaType === "image" ? "media.png" : mediaFile.name);
            form.append("overlay", overlayBlob, "overlay.png");
            form.append("mediaType", mediaType);
            if (musicAssetId) form.append("musicAssetId", musicAssetId);

            const headers = await authHeaders();
            const { data } = await axios.post(`${base_url}/tiktok/compose`, form, { headers });
            if (data.success) {
                setComposedUrl(data.videoUrl);
                setStatus("Composed! Preview below, then post when ready.");
            } else {
                setStatus(`Compose failed: ${data.message}`);
            }
        } catch (e) {
            setStatus(`Error composing: ${e.response?.data?.message || e.message}`);
        } finally {
            setLoading(l => ({ ...l, compose: false }));
        }
    };

    const handlePost = async () => {
        if (!composedUrl) return;
        setLoading(l => ({ ...l, post: true }));
        setStatus("");
        try {
            const headers = await authHeaders();
            const { data } = await axios.post(`${base_url}/tiktok/post`, {
                videoUrl: composedUrl, title: text.trim()
            }, { headers });
            if (data.success) {
                setStatus("Posted to TikTok! It may take a minute to process.");
            } else {
                setStatus(`Failed to post: ${data.message}`);
            }
        } catch (e) {
            setStatus(`Error posting: ${e.response?.data?.message || e.message}`);
        } finally {
            setLoading(l => ({ ...l, post: false }));
        }
    };

    const cardStyle = {
        backgroundColor: "#1e1e3a",
        border: "1px solid #333",
        borderRadius: "8px",
        padding: "20px",
        marginBottom: "20px"
    };

    const btnStyle = (color = "#00d4ff", disabled = false) => ({
        backgroundColor: disabled ? "#444" : color,
        color: "#fff",
        border: "none",
        padding: "8px 18px",
        borderRadius: "4px",
        cursor: disabled ? "not-allowed" : "pointer",
        fontSize: "14px",
        opacity: disabled ? 0.6 : 1,
        marginRight: "8px"
    });

    const inputStyle = {
        width: "100%",
        padding: "8px",
        borderRadius: "4px",
        border: "1px solid #555",
        background: "#111",
        color: "#ddd",
        fontSize: "13px",
        boxSizing: "border-box",
        marginBottom: "10px"
    };

    const isError = status.toLowerCase().includes("fail") || status.toLowerCase().includes("error");

    return (
        <div style={{ minHeight: "100vh", background: "#0d0d1a", color: "#ddd", fontFamily: "Verdana, sans-serif" }}>
            <Header />
            <div style={{ padding: "24px" }}>
                <h2 style={{ color: "#00d4ff", marginBottom: "24px" }}>TikTok Post</h2>

                {connected === false && (
                    <div style={{ ...cardStyle, backgroundColor: "#2a1a1a", borderColor: "#4a2a2a", color: "#f88" }}>
                        No TikTok account connected on Zernio. Connect it in your Zernio dashboard, then reload this page.
                    </div>
                )}

                {status && (
                    <div style={{ ...cardStyle, backgroundColor: isError ? "#2a1a1a" : "#1a2a1a", borderColor: isError ? "#4a2a2a" : "#2a4a2a", color: isError ? "#f88" : "#8f8" }}>
                        {status}
                    </div>
                )}

                <div style={cardStyle}>
                    <h3 style={{ color: "#00d4ff", marginTop: 0 }}>Workout Caption</h3>
                    <input type="date" value={date} onChange={handleDateChange} style={{ ...inputStyle, maxWidth: "200px" }} />
                    {loading.workout ? (
                        <p style={{ color: "#aaa" }}>Loading workout...</p>
                    ) : (
                        <textarea
                            value={text}
                            onChange={e => setText(e.target.value)}
                            rows={6}
                            placeholder={workout ? "" : "No workout found for this date — write your own caption."}
                            style={{ ...inputStyle, resize: "vertical", fontFamily: "Verdana, sans-serif" }}
                        />
                    )}
                </div>

                <div style={cardStyle}>
                    <h3 style={{ color: "#00d4ff", marginTop: 0 }}>Media</h3>
                    <input type="file" accept="image/*,video/*" onChange={handleFileChange} style={inputStyle} />
                    <p style={{ color: "#888", fontSize: "12px", marginTop: 0 }}>
                        Images get a pixel-art filter applied in your browser (via pixelit). Videos are pixelated on the server.
                    </p>

                    <img ref={imgRef} alt="" style={{ display: "none" }} />

                    {mediaType === "image" && (
                        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                            <div>
                                <p style={{ color: "#888", fontSize: "12px" }}>Original</p>
                                {previewUrl && <img src={previewUrl} alt="original" style={{ maxWidth: "260px", borderRadius: "4px" }} />}
                            </div>
                            <div>
                                <p style={{ color: "#888", fontSize: "12px" }}>Pixelated preview</p>
                                <canvas ref={canvasRef} style={{ maxWidth: "260px", borderRadius: "4px", background: "#000" }} />
                                {!pixelPreviewUrl && <p style={{ color: "#aaa", fontSize: "12px" }}>Rendering...</p>}
                            </div>
                        </div>
                    )}

                    {mediaType === "video" && previewUrl && (
                        <video src={previewUrl} controls style={{ maxWidth: "300px", borderRadius: "4px" }} />
                    )}
                </div>

                <div style={cardStyle}>
                    <h3 style={{ color: "#00d4ff", marginTop: 0 }}>Music</h3>
                    <select value={musicAssetId} onChange={e => setMusicAssetId(e.target.value)} style={inputStyle}>
                        <option value="">No music</option>
                        {musicAssets.map(a => (
                            <option key={a._id} value={a._id}>{a.name}</option>
                        ))}
                    </select>

                    <form onSubmit={handleAddTrack} style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                        <input
                            type="text"
                            value={newTrackName}
                            onChange={e => setNewTrackName(e.target.value)}
                            placeholder="Track name"
                            style={{ ...inputStyle, width: "160px", marginBottom: 0 }}
                        />
                        <input
                            type="text"
                            value={newTrackUrl}
                            onChange={e => setNewTrackUrl(e.target.value)}
                            placeholder="Direct audio file URL"
                            style={{ ...inputStyle, flex: 1, minWidth: "220px", marginBottom: 0 }}
                        />
                        <button type="submit" style={btnStyle("#0085ff", loading.track || !newTrackName.trim() || !newTrackUrl.trim())} disabled={loading.track || !newTrackName.trim() || !newTrackUrl.trim()}>
                            {loading.track ? "Adding..." : "Add Track"}
                        </button>
                    </form>
                </div>

                <div style={cardStyle}>
                    <h3 style={{ color: "#00d4ff", marginTop: 0 }}>Compose &amp; Post</h3>
                    <button
                        type="button"
                        onClick={handleCompose}
                        style={btnStyle("#0085ff", loading.compose || !mediaFile || (mediaType === "image" && !pixelPreviewUrl))}
                        disabled={loading.compose || !mediaFile || (mediaType === "image" && !pixelPreviewUrl)}
                    >
                        {loading.compose ? "Composing..." : "Compose Video"}
                    </button>

                    {composedUrl && (
                        <div style={{ marginTop: "16px" }}>
                            <video src={composedUrl} controls style={{ maxWidth: "300px", borderRadius: "4px", display: "block", marginBottom: "12px" }} />
                            <button
                                type="button"
                                onClick={handlePost}
                                style={btnStyle("#00d4ff", loading.post || connected === false)}
                                disabled={loading.post || connected === false}
                            >
                                {loading.post ? "Posting..." : "Post to TikTok"}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TikTokPost;
